import { useCallback, useEffect, useMemo, useState } from "react";
import { t } from "@lingui/macro";
import {
  postRoute,
  useCloudContext,
  useConfigContext,
  useOfflineSWR,
  useSnackbarContext,
} from "@WahooFitness/wahoo-offline-mfe";
import { Assessments, FitnessProfiles } from "@WahooFitness/cloud-client-ts";
import {
  FitnessProfileAlgorithm,
  WahooWorkoutTypeFamily,
  FitnessProfileLogResponseType,
} from "@WahooFitness/cloud-client-types";
import useLocalStorage from "@/hooks/useLocalStorage";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import { EventType, ClickTarget, SourcePage } from "@/services/Analytics/EventTypes";
import shouldShowFitnessAssessmentPrompt from "./shouldShowFitnessAssessmentPrompt";

export type HistoricalProfile = FitnessProfileLogResponseType & {
  previousProfile?: FitnessProfileLogResponseType;
};

export const useHistoricalFitnessProfiles = () => {
  const { getCloudClient } = useCloudContext();
  const { wahooToken } = useConfigContext();
  const { enqueueSnackbar } = useSnackbarContext();
  const [openId, setOpenId] = useState<number | undefined>();

  const profilesClient = getCloudClient(FitnessProfiles);
  const assessmentsClient = getCloudClient(Assessments);

  const {
    data: historicalProfilesData = [],
    isLoading: historicalProfilesLoading,
    error: historicalProfilesError,
  } = useOfflineSWR(["fitnessProfileLogs", wahooToken], async ([_key, wahooToken]) => {
    return profilesClient.getFitnessProfileLogsByWorkoutFamily(
      wahooToken,
      WahooWorkoutTypeFamily.Cycling
    );
  });

  const {
    data: userFitnessProfilesData,
    isLoading: userFitnessProfilesIsLoading,
    error: userFitnessProfilesError,
  } = useOfflineSWR(["userFitnessProfiles", wahooToken], ([_key, token]) =>
    profilesClient.get(token)
  );

  const cyclingFitnessProfilesData = useMemo(
    () =>
      userFitnessProfilesData?.find(
        (profile) => profile.workout_type_family_id === WahooWorkoutTypeFamily.Cycling
      ),
    [userFitnessProfilesData]
  );

  const {
    data: assessmentsData = [],
    isLoading: assessmentsLoading,
    error: assessmentsError,
  } = useOfflineSWR(["assessments", wahooToken], async ([_key, wahooToken]) => {
    return assessmentsClient.get(wahooToken);
  });

  const historicalProfilesDataWithPrevious: HistoricalProfile[] = useMemo(() => {
    const sortedHistoricProfiles = historicalProfilesData
      // filter out profiles that are not assessments or have no assessment_id (these are from the initial migration)
      .filter(
        (profile) =>
          !(
            [
              FitnessProfileAlgorithm.FullFrontalAssessment,
              FitnessProfileAlgorithm.HalfMontyAssessment,
            ].includes(profile.fitness_profile_algorithm_id) && !profile.assessment_id
          )
      )
      // Sort by most recent first
      .sort((a, b) => {
        const dateA = new Date(a.created_at ?? 0);
        const dateB = new Date(b.created_at ?? 0);
        return dateB.getTime() - dateA.getTime();
      });
    const historicalProfilesWithCurrent = [
      (cyclingFitnessProfilesData as HistoricalProfile) || ({} as HistoricalProfile),
      ...sortedHistoricProfiles,
    ].map((profile, index, arr) => {
      if (index === arr.length - 1) {
        return profile; // No previous data for the oldest profile
      }
      const previousProfile = arr[index + 1];
      return {
        ...profile,
        previousProfile,
      };
    });
    return historicalProfilesWithCurrent;
  }, [cyclingFitnessProfilesData, historicalProfilesData]);

  const initialFilterButtons = useMemo(
    () => [
      { label: "Full Frontal", key: String(FitnessProfileAlgorithm.FullFrontalAssessment) },
      { label: "Half Monty", key: String(FitnessProfileAlgorithm.HalfMontyAssessment) },
      { label: t`Questionnaire`, key: String(FitnessProfileAlgorithm.FourDPQuiz) },
      { label: t`Manual`, key: String(FitnessProfileAlgorithm.Custom) },
      { label: t`SYSTM Onboarding`, key: String(FitnessProfileAlgorithm.SystmOnboarding) },
    ],
    []
  );

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const { logEvent } = useLogEvent();

  const { setValue: setFitnessTestPromptLastSeen, data: fitnessAssessmentLastSeenDate } =
    useLocalStorage("fitnessAssessmentLastSeenDate");

  const handleAccordionChange = (id: number) => {
    setOpenId((prev) => (prev === id ? undefined : id));
    logEvent("historical_fitness_profile", {
      event_type: EventType.click,
      click_target: ClickTarget.historical_fitness_profile,
      source_page_name: SourcePage.historical_fitness_profiles,
    });
  };

  const handleViewWorkoutDetails = (assessmentId: number) => {
    const assessment = assessmentsData.find((a) => a.id === assessmentId);
    if (!assessment) {
      enqueueSnackbar({
        message: t`Sorry, we couldn't find the workout details for this assessment.`,
        severity: "error",
      });
      return;
    }
    logEvent("historical_fitness_profile", {
      event_type: EventType.click,
      click_target: ClickTarget.view_workout_details,
      source_page_name: SourcePage.historical_fitness_profiles,
    });
    postRoute("activity", String(assessment.workout_summary_id));
  };

  const filteredHistoricalData = useMemo<HistoricalProfile[]>(() => {
    return historicalProfilesDataWithPrevious.filter(
      (item) =>
        selectedFilters.length === 0 ||
        selectedFilters.includes(String(item.fitness_profile_algorithm_id))
    );
  }, [historicalProfilesDataWithPrevious, selectedFilters]);

  const handleFitnessAssessmentClose = useCallback(() => {
    const currentDate = new Date().toISOString();
    setFitnessTestPromptLastSeen(currentDate);
    logEvent("historical_fitness_profile", {
      event_type: EventType.dismiss,
      click_target: ClickTarget.retake_fitness_assessment,
      source_page_name: SourcePage.historical_fitness_profiles,
    });
  }, [logEvent, setFitnessTestPromptLastSeen]);

  const showAssessmentPrompt = useMemo(() => {
    const lastFitnessTestDate = assessmentsData.reduce((latest, assessment) => {
      const assessmentDate = new Date(assessment.started_at ?? 0);
      return assessmentDate > latest ? assessmentDate : latest;
    }, new Date(0));
    return shouldShowFitnessAssessmentPrompt(
      lastFitnessTestDate.toISOString(),
      fitnessAssessmentLastSeenDate
    );
  }, [assessmentsData, fitnessAssessmentLastSeenDate]);

  useEffect(() => {
    if (filteredHistoricalData.length) {
      setOpenId(filteredHistoricalData[0].id);
    }
  }, [filteredHistoricalData]);

  const handleFilterChange = (key: string) => {
    setSelectedFilters((prev) => {
      if (prev.includes(key)) {
        return prev.filter((filter) => filter !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  const filterButtons = useMemo(() => {
    const uniqueTypes = Array.from(
      new Set(
        historicalProfilesDataWithPrevious.map((profile) =>
          String(profile.fitness_profile_algorithm_id)
        )
      )
    );

    if (uniqueTypes.length <= 1) return [];

    return initialFilterButtons.filter((button) => uniqueTypes.includes(String(button.key)));
  }, [historicalProfilesDataWithPrevious, initialFilterButtons]);

  return {
    historicalProfilesData: historicalProfilesDataWithPrevious,
    openId,
    handleAccordionChange,
    handleViewWorkoutDetails,
    selectedFilters,
    handleFilterChange,
    filteredHistoricalData,
    handleFitnessAssessmentClose,
    showAssessmentPrompt,
    filterButtons,
    isLoading: historicalProfilesLoading || assessmentsLoading || userFitnessProfilesIsLoading,
    error: historicalProfilesError || assessmentsError || userFitnessProfilesError,
  };
};
